import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Tags } from "lucide-react";
import { Card, CardHeader, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import SportCategoryChip from "components/SportCategoryChip";
import { useAuth } from "contexts/AuthContext";
import { useI18n } from "i18n";
import api from "lib/api";
import { CHIP_PRESETS, DEFAULT_CHIP, matchPreset } from "lib/chipPresets";
import { cn } from "lib/utils";

const SELECT_CLASS =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm " +
  "ring-offset-background focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-ring focus-visible:ring-offset-2";

export default function SportCategoryForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useI18n();
  const isEditing = !!id;

  const [form, setForm] = useState({
    // "Nova categoria" a partir da lista filtrada ja vem com a modalidade.
    sport: location.state?.sport || "",
    name: "",
    background_color: DEFAULT_CHIP.background,
    text_color: DEFAULT_CHIP.text,
  });
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);
  const [error, setError] = useState("");

  const canManage = !!user?.capabilities?.manage_events;

  useEffect(() => {
    api
      .get("/api/sports/")
      .then((res) => setSports(res.data || []))
      .catch(() => setSports([]));
  }, []);

  useEffect(() => {
    if (!isEditing) return;
    api
      .get(`/api/sport-categories/${id}/`)
      .then((res) =>
        setForm({
          sport: res.data.sport,
          name: res.data.name,
          background_color: res.data.background_color,
          text_color: res.data.text_color,
        })
      )
      .catch(() => setError(t("common.loadError")))
      .finally(() => setFetching(false));
  }, [id, isEditing, t]);

  if (user && !canManage) {
    return <Navigate to="/app/dashboard" replace />;
  }

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const applyPreset = (preset) =>
    setForm((prev) => ({
      ...prev,
      background_color: preset.background,
      text_color: preset.text,
    }));

  const activePreset = matchPreset(form.background_color, form.text_color);
  const backTo = form.sport
    ? `/app/sport-categories?sport=${form.sport}`
    : "/app/sport-categories";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isEditing) {
        await api.put(`/api/sport-categories/${id}/`, form);
      } else {
        await api.post("/api/sport-categories/", form);
      }
      navigate(backTo);
    } catch (err) {
      const detail = err.response?.data;
      setError(
        typeof detail === "object"
          ? Object.values(detail).flat().join(" ")
          : t("common.saveError")
      );
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="w-full animate-fade-in">
      <button
        onClick={() => navigate(backTo)}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("common.back")}
      </button>

      <Card className="w-full">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Tags className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight">
                {isEditing ? t("sportCategory.edit") : t("sportCategory.new")}
              </h1>
              <p className="text-sm text-muted-foreground">
                {isEditing
                  ? t("sportCategory.editSubtitle")
                  : t("sportCategory.newSubtitle")}
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="w-full space-y-6">
            {error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="sport">{t("event.sport")}</Label>
                <select
                  id="sport"
                  value={form.sport}
                  onChange={set("sport")}
                  required
                  className={SELECT_CLASS}
                >
                  <option value="" disabled>
                    {t("event.sportPlaceholder")}
                  </option>
                  {sports.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="name">{t("sportCategory.name")}</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={set("name")}
                  required
                  maxLength={120}
                  placeholder={t("sportCategory.namePlaceholder")}
                />
              </div>
            </div>

            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-muted-foreground">
                {t("sportCategory.colors")}
              </h2>

              {/* Presets: um clique preenche as duas cores; o chip de cada
                  botao ja mostra como vai ficar. */}
              <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
                {CHIP_PRESETS.map((preset) => {
                  const active = activePreset?.id === preset.id;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => applyPreset(preset)}
                      aria-pressed={active}
                      className={cn(
                        "flex items-center justify-between gap-2 rounded-lg border p-2 text-left transition-colors hover:bg-muted/50",
                        active ? "border-primary ring-1 ring-primary" : "border-border"
                      )}
                    >
                      <SportCategoryChip
                        chip={{
                          name: t(`chipPreset.${preset.id}`),
                          background_color: preset.background,
                          text_color: preset.text,
                        }}
                        className="border border-black/5"
                      />
                      {active && <Check className="h-4 w-4 shrink-0 text-primary" />}
                    </button>
                  );
                })}
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <ColorField
                  id="background_color"
                  label={t("sportCategory.backgroundColor")}
                  value={form.background_color}
                  onChange={(value) =>
                    setForm((prev) => ({ ...prev, background_color: value }))
                  }
                />
                <ColorField
                  id="text_color"
                  label={t("sportCategory.textColor")}
                  value={form.text_color}
                  onChange={(value) =>
                    setForm((prev) => ({ ...prev, text_color: value }))
                  }
                />
                <div>
                  <Label>{t("sportCategory.preview")}</Label>
                  {/* Sobre uma foto escura e uma clara: o chip vive em cima da
                      capa do evento, que pode ser qualquer coisa. */}
                  <div className="flex h-10 overflow-hidden rounded-md border border-border">
                    <div className="flex flex-1 items-center justify-center bg-neutral-800">
                      <SportCategoryChip
                        chip={{ ...form, name: form.name || t("sportCategory.namePlaceholder") }}
                        className="py-0.5"
                      />
                    </div>
                    <div className="flex flex-1 items-center justify-center bg-neutral-200">
                      <SportCategoryChip
                        chip={{ ...form, name: form.name || t("sportCategory.namePlaceholder") }}
                        className="py-0.5"
                      />
                    </div>
                  </div>
                </div>
              </div>
              {!activePreset && (
                <p className="text-xs text-muted-foreground">
                  {t("sportCategory.customColors")}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3 border-t border-border pt-4">
              <Button type="button" variant="outline" onClick={() => navigate(backTo)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" loading={loading}>
                {isEditing ? t("common.save") : t("common.create")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

/** Seletor nativo de cor + o hex editavel ao lado (colar uma cor da marca). */
function ColorField({ id, label, value, onChange }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);

  const commit = (raw) => {
    setText(raw);
    const hex = raw.startsWith("#") ? raw : `#${raw}`;
    if (/^#[0-9A-Fa-f]{6}$/.test(hex)) onChange(hex.toUpperCase());
  };

  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          aria-label={label}
          className="h-10 w-12 shrink-0 cursor-pointer rounded-md border border-input bg-background p-1"
        />
        <Input
          id={id}
          value={text}
          onChange={(e) => commit(e.target.value.trim())}
          maxLength={7}
          className="font-mono uppercase"
        />
      </div>
    </div>
  );
}
